import { t } from "../../i18n";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useProjectStore, useSelectedWall } from "../../state/projectStore";
import { useCadUi } from "../../state/cadUiStore";
import { useAnalysis } from "../../state/useAnalysis";
import { useAutoSizeStore } from "../../state/useAutoSize";
import { useWizardStore } from "../../state/wizardStore";
import { useFullAccess } from "../../billing/access";
import { geometryEdited, guideSteps, scanFresh, soilEntered, type GuideStep, type StepId } from "./steps";

const LABEL: Record<StepId, { long: string; short: string }> = {
  soil: { long: t("Zemin ve yükler"), short: t("Zemin") }, size: { long: t("Boyutlandır"), short: t("Boyut") },
  pick: { long: t("Seç ve uygula"), short: t("Seç") }, out: { long: t("Rapor / Çizim"), short: t("Rapor") },
};
const NUM = ["①", "②", "③", "④"] as const;

/**
 * Akıllı proje adım çubuğu (Cem 2026-10-06, ~28 px, tek satır): ① Zemin ve yükler → ② Boyutlandır → ③ Seç ve uygula →
 * ④ Rapor / Çizim. Tamamlanan ✓, sıradaki ➡️ ile vurgulu; ③ duvar sağlamıyorsa kırmızı nokta. Telefonda kısa etiket, kaydırmasız; 40 px (dokunma hedefi 36 px).
 * Paylaşım (salt okunur) görünümünde gizli; ✕ ile kapatılır (tercih localStorage, sihirbaz açılınca yeniden görünür).
 */
export function Stepper(): JSX.Element | null {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const project = useProjectStore((s) => s.project), origin = useProjectStore((s) => s.origin), readOnly = useProjectStore((s) => s.readOnly);
  const wall = useSelectedWall();
  const layout = useCadUi((s) => s.layout);
  const hidden = useWizardStore((s) => s.stepperHidden), setHidden = useWizardStore((s) => s.setStepperHidden);
  const soilDone = useWizardStore((s) => (wall ? !!s.soilDone[wall.id] : false));
  const reportSeen = useWizardStore((s) => (project ? !!s.reportSeen[project.id] : false));
  const auto = useAutoSizeStore();
  const full = useFullAccess();
  const { result } = useAnalysis(wall);
  const onReport = pathname.endsWith("/rapor");
  // ④: rapor rotası ya da Çizim paftası bu projede bir kez görüldü
  useEffect(() => { if (project && (onReport || layout === "drawing")) useWizardStore.getState().markReport(project.id); }, [project?.id, onReport, layout]);
  if (!project || !wall || origin === "shared" || hidden) return null;

  const height = auto.inputs[wall.id]?.height ?? (result ? Math.round(result.geometry.yTop * 100) / 100 : 3);
  const steps = guideSteps({ soil: soilDone || soilEntered(wall), scan: scanFresh(auto, wall, height), ok: result ? result.ok : null, edited: geometryEdited(wall), report: reportSeen });
  const toProject = (): void => { if (onReport) nav(`/proje/${project.id}`); };
  const go = (id: StepId): void => {
    const cad = useCadUi.getState();
    if (id === "soil") { if (!readOnly) useWizardStore.getState().open(wall.id); return; }
    if (id === "out") { cad.setRibbonTab("report"); nav(`/proje/${project.id}/rapor`); return; }
    cad.setLayout("design"); toProject();
  };
  const tip = (s: GuideStep): string => {
    if (s.id === "pick" && s.bad) return t("Kontrol sağlanmıyor — Akıllı tasarımla boyutlandırın");
    if (s.id === "soil") return readOnly ? t("Salt okunur: sihirbaz açılamaz") : t("Sihirbaz: zemin raporu, zemin türü, yükler, su ve deprem");
    if (s.id === "size") return full ? t("Akıllı tasarım: taş ve betonarme kesiti otomatik boyutlandır") : `🔒 ${t("Akıllı tasarım aboneliğe dahil")}`;
    if (s.id === "pick") return t("Akıllı tasarım: en uygun kesiti seçip duvara uygulayın");
    return t("Raporu aç (Çizim paftası da bu adımı tamamlar)");
  };

  return (
    <nav aria-label={t("Akıllı proje adımları")} data-testid="guide-stepper" className="cad-stepper no-print shrink-0 flex items-center gap-1 h-7 max-md:h-[40px] px-2 max-md:px-1 bg-cad-chrome2 border-b border-cad-line text-[11px] text-cad-ink2 min-w-0 overflow-hidden">
      <span className="max-md:hidden shrink-0 text-cad-ink3 mr-1">✨ {t("Akıllı proje")}:</span>
      <ol className="flex items-center gap-0.5 min-w-0 flex-1 m-0 p-0 list-none">
        {steps.map((s, k) => (
          <li key={s.id} className="flex items-center gap-0.5 min-w-0">
            {k > 0 ? <span aria-hidden className="text-cad-ink4 px-0.5">›</span> : null}
            <button type="button" onClick={() => go(s.id)} title={tip(s)} disabled={s.id === "soil" && readOnly}
              data-step={s.id} data-done={s.done || undefined} data-next={s.next || undefined} aria-current={s.next ? "step" : undefined}
              aria-label={`${s.n}. ${LABEL[s.id].long}${s.done ? ` — ${t("tamam")}` : s.next ? ` — ${t("sıradaki adım")}` : ""}${s.id === "pick" && s.bad ? ` — ${t("kontrol sağlanmıyor")}` : ""}`}
              className={`relative inline-flex items-center gap-1 h-[22px] max-md:h-[36px] px-1.5 rounded-[3px] border whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-default ${s.next ? "bg-[#EAF1FB] border-[#7EA6D8] text-cad-accent font-semibold" : s.done ? "border-transparent text-[#1D7A46]" : "border-transparent hover:bg-cad-hover hover:border-cad-hoverline"}`}>
              <span aria-hidden>{s.done ? "✓" : s.next ? "➡️" : NUM[k]}</span>
              <span aria-hidden className="md:hidden">{LABEL[s.id].short}</span>
              <span aria-hidden className="max-md:hidden">{LABEL[s.id].long}</span>
              {s.id === "size" && !full ? <span aria-hidden className="text-[9px]">🔒</span> : null}
              {s.id === "pick" && s.bad ? <span aria-hidden data-testid="step-bad" className="w-1.5 h-1.5 rounded-full bg-[#B42318] shrink-0" /> : null}
            </button>
          </li>
        ))}
      </ol>
      <button type="button" aria-label={t("Adım çubuğunu gizle")} title={t("Adım çubuğunu gizle (Başlangıç › Tasarım › Sihirbaz açılınca yeniden görünür)")} className="shrink-0 w-[22px] h-[22px] max-md:w-[36px] max-md:h-[36px] inline-flex items-center justify-center text-cad-ink3 hover:text-cad-ink cursor-pointer" onClick={() => setHidden(true)}>✕</button>
    </nav>
  );
}
